import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { cn } from "@/modules/lib/utils"
import { BoxLeadFormHeader } from "@modules/boxLead/components/boxLeadForm/boxLeadFormHeader"
import { BoxLeadFormStepOne } from "@modules/boxLead/components/boxLeadForm/boxLeadFormStepOne"
import { BoxLeadFormStepTwo } from "@modules/boxLead/components/boxLeadForm/boxLeadFormStepTwo"
import { BoxLeadEntity } from "@modules/boxLead/entities/boxLeadEntity"
import { BoxLeadDraftEntity } from "@modules/boxLead/entities/boxLeadInput"
import { addLeadYupValidationSchema, type BoxLeadInputKey } from "@modules/boxLead/formik/boxLeadFormik"
import { useBoxLeadCreate, useBoxLeadUpdate } from "@modules/boxLead/hooks/useBoxLeadActions"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useFormik } from "formik"
import { startTransition, useState } from "react"

type BoxLeadFormLayoutProps = {
  boxLead?: BoxLeadEntity
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadFormLayout({ boxLead, className, ...props }: BoxLeadFormLayoutProps) {
  const { hideSidebar } = useSidebar()
  const [stepNumber, setStepNumber] = useState(1)
  const { createBoxLead } = useBoxLeadCreate()
  const { updateBoxLead } = useBoxLeadUpdate()

  const navigateStep = (onNext: boolean) => {
    startTransition(() => {
      setStepNumber((prev) => (onNext ? prev + 1 : prev - 1))
    })
  }

  const handleClose = () => {
    hideSidebar(false)
  }

  const formik = useFormik<BoxLeadDraftEntity>({
    initialValues: BoxLeadDraftEntity.createDraft(boxLead),
    validationSchema: addLeadYupValidationSchema(stepNumber),
    validateOnBlur: false,
    validateOnMount: false,
    onSubmit: async () => {
      if (stepNumber < 2) {
        navigateStep(true)
        return
      }
      if (boxLead) {
        await updateBoxLead({
          leadId: boxLead.id,
          leadDraft: formik.values,
        })
          .then(() => {
            toast.success(word("box.lead.form.toast.edit.success"), {
              classNames: successToastClassName(),
            })
            hideSidebar(false)
          })
          .catch(() => {
            toast.error(word("box.lead.form.toast.edit.error"), {
              classNames: errorToastClassName(),
            })
          })
        return
      }
      await createBoxLead(formik.values)
        .then(() => {
          toast.success(word("box.lead.form.toast.add.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar(false)
        })
        .catch((error) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (error.status === 409) {
            toast.error(word("box.lead.form.toast.add.errorAlreadyExists"), {
              classNames: errorToastClassName(),
            })
          } else {
            toast.error(word("box.lead.form.toast.add.error"), {
              classNames: errorToastClassName(),
            })
          }
        })
    },
  })

  return (
    <div className={cn("flex flex-col gap-8 px-5 pt-15 pb-8", className)} {...props}>
      <BoxLeadFormHeader isEdition={!!boxLead} />

      {stepNumber === 1 && (
        <BoxLeadFormStepOne
          handleSubmit={formik.handleSubmit}
          onPrevious={handleClose}
          getFormErrorMessage={(input: BoxLeadInputKey) => getFormErrorMessage(input, formik)}
          formik={formik}
          disabled={!addLeadYupValidationSchema(stepNumber).isValidSync(formik.values)}
        />
      )}
      {stepNumber === 2 && (
        <BoxLeadFormStepTwo
          handleSubmit={formik.handleSubmit}
          onPrevious={() => navigateStep(false)}
          getFormErrorMessage={(input: BoxLeadInputKey) => getFormErrorMessage(input, formik)}
          isSubmitting={formik.isSubmitting}
          formik={formik}
          disabled={!addLeadYupValidationSchema(stepNumber).isValidSync(formik.values)}
          isEdition={!!boxLead}
          user={boxLead?.user}
        />
      )}
    </div>
  )
}
